import "~/styles/login.css"

import { ArrowRightOutlined, GoogleOutlined } from "@ant-design/icons"
import { Button, ConfigProvider, Form, Image, Input, theme } from "antd"
import { ArrowLeft, Edit3Icon, Loader } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  ActionFunction,
  Link,
  LinksFunction,
  LoaderFunction,
  MetaFunction,
  useFetcher,
  useSearchParams
} from "react-router"

import ButtonPrimary from "~/components/base-button"
import { BaseInputPassword } from "~/components/base-input"
import AzureAd from "~/components/icons/azure-ad"
import OSAlert from "~/components/os-alert"
import { loginAction, loginLoader } from "~/loaders-actions/login"
import { axiosInstance } from "~/utils/axios"

export const links: LinksFunction = () => [
  {
    rel: "prefetch",
    type: "image/png",
    href: "https://dmi3q4vtj7try.cloudfront.net/login-image.webp"
  }
]

export const meta: MetaFunction = () => [{ title: `OrbitShift | Login` }]

export const action: ActionFunction = loginAction
export const loader: LoaderFunction = loginLoader

export default function Login() {
  const fetcher = useFetcher()
  const [form] = Form.useForm()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get("redirectTo") || ""
  const credentialsRef = useRef({
    email: "",
    password: ""
  })
  const [error, setError] = useState<string | null>(null)
  const [generatingSsoLink, setGeneratingSsoLink] = useState({
    type: "",
    loading: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResendOtp, setIsResendOtp] = useState(false)
  const [connections, setConnections] = useState<null | { name: string }[]>(
    null
  )

  const isOtpRequestSucess =
    (fetcher.data?.success || fetcher.data?.error) &&
    fetcher.data?.data?.request_otp

  const handleBackButton = useCallback(() => {
    history.pushState(null, "", window.location.href)
  }, [])

  useEffect(() => {
    history.pushState(null, "", window.location.href)
    window.addEventListener("popstate", handleBackButton)
    window.addEventListener("beforeunload", handleBackButton)
    sessionStorage.clear()
    localStorage.removeItem("agent_account")

    return () => {
      window.removeEventListener("popstate", handleBackButton)
      window.removeEventListener("beforeunload", handleBackButton)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFinish = async (values: {
    _email: string
    _password: string
    otp: string
  }) => {
    setIsSubmitting(true)

    if (!connections || connections?.length === 0) {
      await fetcher.submit(
        {
          email: values._email,
          action_type: "get_organisations",
          redirectTo
        },
        { method: "post", action: "/login" }
      )
      credentialsRef.current = {
        email: values._email,
        password: ""
      }
    } else {
      if (!isOtpRequestSucess) {
        form.setFieldsValue({
          otp: ""
        })
        credentialsRef.current = {
          email: values._email,
          password: values._password
        }
      }
      await fetcher.submit(
        {
          email: values._email || credentialsRef.current.email,
          password: values._password || credentialsRef.current.password,
          request_otp: isOtpRequestSucess ? "false" : "true",
          otp: values.otp,
          redirectTo
        },
        { method: "post", action: "/login" }
      )
    }
    setIsSubmitting(false)
  }

  const resendOtp = async () => {
    await fetcher.submit(
      {
        email: credentialsRef.current.email,
        password: credentialsRef.current.password,
        request_otp: "true",
        resend_otp: "true",
        redirectTo
      },
      { method: "post", action: "/login" }
    )
    form.setFieldsValue({
      otp: ""
    })
    setIsResendOtp(true)
  }

  const resetForm = () => {
    fetcher.submit(
      {
        reset_form: "true"
      },
      { method: "post", action: "/login" }
    )
    setConnections(null)
    credentialsRef.current = {
      email: "",
      password: ""
    }
    form.setFieldValue("_password", "")
    form.setFieldValue("otp", "")
  }

  useEffect(() => {
    if (!isResendOtp) return
    const timer = setTimeout(() => {
      setIsResendOtp(false)
    }, 5000)
    return () => clearTimeout(timer)
  }, [isResendOtp])

  useEffect(() => {
    if (!fetcher.data?.data?.connections) return
    setConnections(fetcher.data?.data?.connections)
  }, [fetcher.data?.data?.connections])

  const filteredConnections = connections?.filter(
    (connection: { name: string }) => connection.name !== "email"
  )

  const isEmailConnection = connections?.some(
    (connection: { name: string }) => connection.name === "email"
  )

  const onMicrosoftClick = async () => {
    try {
      setGeneratingSsoLink({
        type: "microsoft",
        loading: true
      })
      const response = await axiosInstance("sso/login", {
        method: "POST",
        data: {
          connection: "microsoft",
          email: credentialsRef.current.email
        }
      })
      const authUrl = response.data.data?.authUrl
      window.location.href = authUrl
    } catch {
      setError("Failed to connect Microsoft account")
      setGeneratingSsoLink({
        type: "microsoft",
        loading: false
      })
    }
  }

  const isPending =
    isSubmitting ||
    fetcher.state === "loading" ||
    fetcher.data?.data?.redirectUrl

  const fetcherError = isPending
    ? ""
    : (fetcher.data as { error: string })?.error

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <div className="flex h-screen min-h-[700px] w-screen bg-[#F4F7FA] overflow-hidden">
        <div className="flex-1 flex flex-col justify-center items-center left-section_bg py-8 relative">
          <img
            src={"/orbitshift_logo.svg"}
            alt="OrbitShift"
            width="140"
            height="33"
            className="absolute top-8 left-8"
          />
          <div
            className={`border border-gray-300 rounded-2xl max-w-lg w-[440px] max-md:w-[360px] max-lg:w-[360px] overflow-hidden flex flex-col gap-4 shadow-lg p-6 bg-white ${fetcherError || error ? "!border-red-400" : ""}`}
          >
            <div className="flex flex-col gap-1">
              <h1 className="text-dark text-xl font-semibold">
                Log into OrbitShift account
              </h1>
              <p className="text-xs text-gray-500">
                Enter email registered on OrbitShift
              </p>
            </div>
            {isOtpRequestSucess && (
              <p className="text-dark-2 text-13 font-medium text-center">
                Please enter the verification code that was sent to your email.
                The code will be valid for 30 minutes.
              </p>
            )}
            <Form
              layout="vertical"
              autoComplete="off"
              spellCheck={false}
              className="login-form"
              method="post"
              onFinish={onFinish}
              form={form}
            >
              {(fetcherError || error) && (
                <p className="text-red-500 text-xs font-medium mb-2">
                  {fetcherError || error}
                </p>
              )}
              {isOtpRequestSucess ? (
                <fieldset className="px-3 py-4 border border-light-border rounded-lg flex mb-4">
                  <legend className="!text-dark-2 !px-1 !w-auto !text-13 !font-medium !border-none !m-0">
                    Enter verification code*
                  </legend>
                  <Form.Item
                    label=""
                    name="otp"
                    className="[&_.ant-otp]:w-full [&_.ant-otp_.ant-otp-input-wrapper]:flex-1 mb-0 w-full px-10"
                    rules={[
                      {
                        required: true,
                        message: "OTP cannot be Empty"
                      }
                    ]}
                  >
                    <Input.OTP length={6} />
                  </Form.Item>
                </fieldset>
              ) : (
                <>
                  <Form.Item
                    label="Email"
                    name="_email"
                    className={"mb-3"}
                    rules={[
                      {
                        required: true,
                        message: "Email Id cannot be Empty"
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email"
                      }
                    ]}
                  >
                    <Input
                      placeholder="Email"
                      autoComplete="off"
                      className={`text-dark-2 h-9 rounded-lg bg-transparent border border-[#D1D5DA] w-full  placeholder:text-[#BFBFBF] ${isPending || (connections?.length || 0) > 0 ? "!bg-[#f5f5f5] hover:border-[#D1D5DA] focus-within:border-[#D1D5DA] focus-within:shadow-none" : "hover:bg-transparent hover:border focus-within:bg-transparent focus-within:border focus-within:border-primary"}`}
                      classNames={{
                        input:
                          "text-dark-2 text-13 bg-transparent w-full placeholder:text-[#BFBFBF]"
                      }}
                      readOnly={isPending || (connections?.length || 0) > 0}
                      suffix={
                        <Edit3Icon
                          size={16}
                          className={
                            (connections?.length || 0) > 0
                              ? "cursor-pointer"
                              : "hidden"
                          }
                          onClick={resetForm}
                        />
                      }
                    />
                  </Form.Item>
                  {filteredConnections?.length === 0 ? (
                    <Form.Item
                      label="Password"
                      name="_password"
                      className="mb-3"
                      rules={[
                        {
                          required: true,
                          message: "Password cannot be Empty"
                        }
                      ]}
                    >
                      <BaseInputPassword
                        placeholder="Password"
                        autoComplete="off"
                        className="h-9 !text-13"
                      />
                    </Form.Item>
                  ) : filteredConnections?.length ? (
                    <div className="flex gap-3 items-center">
                      <ButtonPrimary
                        iconPosition="start"
                        className="h-9 font-medium flex-1"
                        disabled
                      >
                        <GoogleOutlined size={24} />
                        Google
                      </ButtonPrimary>
                      <ButtonPrimary
                        iconPosition="start"
                        className="h-9 font-medium flex-1"
                        onClick={onMicrosoftClick}
                      >
                        {generatingSsoLink.type === "microsoft" &&
                        generatingSsoLink.loading ? (
                          <Loader size={20} className="animate-spin" />
                        ) : null}
                        <AzureAd size={20} className="w-5 h-5" />
                        Microsoft
                      </ButtonPrimary>
                    </div>
                  ) : null}
                </>
              )}
              {!filteredConnections || filteredConnections?.length === 0 ? (
                <ButtonPrimary
                  type="primary"
                  htmlType="submit"
                  className={
                    "w-full h-9 rounded-lg font-medium " +
                    (isPending
                      ? "!cursor-progress !pointer-events-none !bg-primary-light"
                      : "")
                  }
                  icon={isOtpRequestSucess ? null : <ArrowRightOutlined />}
                  iconPosition="end"
                >
                  {isOtpRequestSucess ? "Log In" : "Continue"}
                  {isPending && (
                    <Loader className="ml-2 animate-spin" size={16} />
                  )}
                </ButtonPrimary>
              ) : null}
              {isResendOtp && fetcher.data?.success && (
                <OSAlert
                  type="success"
                  className="font-medium text-13 mt-3"
                  message="OTP has been resent successfully."
                />
              )}
              {isOtpRequestSucess ? (
                <div className="flex items-center justify-between mt-4">
                  <button
                    type="button"
                    className="text-xs flex items-center gap-2 text-blue-600 hover:underline"
                    onClick={resetForm}
                  >
                    <ArrowLeft size={16} />
                    Back to log in
                  </button>
                  {!fetcherError
                    ?.toLowerCase()
                    .includes("max otp requests reached") && (
                    <ResendOtp resendOtp={resendOtp} />
                  )}
                </div>
              ) : (
                <>
                  <p className="text-[11px] text-gray-500 mt-3">
                    By continuing, you agree to OrbitShift&apos;s{" "}
                    <a
                      href="https://www.orbitshift.ai/privacy-policy"
                      className="text-primary font-medium"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of service
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://www.orbitshift.ai/privacy-policy"
                      className="text-primary font-medium whitespace-nowrap"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                  <div className={"flex justify-end mt-4 text-13"}>
                    {!connections && !isEmailConnection ? (
                      <Link
                        to="/register"
                        className="text-primary pb-[0.5px] border-b border-dashed border-primary font-medium"
                        tabIndex={0}
                      >
                        First time sign up
                      </Link>
                    ) : null}
                    {filteredConnections?.length === 0 && isEmailConnection && (
                      <Link
                        to={`/forgot-password?email=${encodeURIComponent(credentialsRef.current.email)}`}
                        className="text-primary pb-[0.5px] border-b border-dashed border-primary font-medium"
                        tabIndex={0}
                      >
                        Forgot password?
                      </Link>
                    )}
                  </div>
                </>
              )}
            </Form>
          </div>
        </div>
        <section className="min-h-screen w-1/2 min-w-[50%] max-w-[50%] flex flex-col relative justify-center items-center">
          <div className="flex flex-col gap-4 absolute top-20 p-6 text-center z-10 max-w-[84%]">
            <h1 className="text-4xl font-semibold text-dark-6">
              The Multi-agent AI System for Enterprise Sales
            </h1>
            <h3 className="text-sm text-center font-medium text-dark-6">
              The most comprehensive AI agent system for actionable account
              insights, RFP response generation, and targeted sales content
            </h3>
          </div>
          <Image
            src="https://dmi3q4vtj7try.cloudfront.net/login-image.webp"
            alt="Login page banner"
            className="w-full h-full"
            loading="eager"
            preview={false}
            placeholder={
              <Image
                preview={false}
                src={"/login-image-placeholder.jpg"}
                width="100%"
              />
            }
          />
        </section>
      </div>
    </ConfigProvider>
  )
}

const TIMER = 60
const ResendOtp = ({ resendOtp }: { resendOtp: () => Promise<void> }) => {
  // const [requestCount, setRequestCount] = useState(1)
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(TIMER)
  const endTimeRef = useRef<number>(Date.now() + TIMER * 1000)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const startTimer = () => {
    endTimeRef.current = Date.now() + TIMER * 1000
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    const tick = () => {
      const now = Date.now()
      const remaining = Math.max(
        0,
        Math.round((endTimeRef.current - now) / 1000)
      )

      setCountdown(remaining)

      if (remaining <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    intervalRef.current = setInterval(tick, 1000)
    tick()
  }

  useEffect(() => {
    startTimer()
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // if (requestCount >= 3) return null

  return (
    <Button
      htmlType="button"
      type="text"
      loading={loading}
      className={`text-xs p-0 h-auto flex hover:underline gap-1 font-medium ${countdown > 0 ? "cursor-not-allowed pointer-events-none text-dark-2" : "text-blue-600"}`}
      onClick={async () => {
        setLoading(true)
        await resendOtp()
        startTimer()
        // setRequestCount(requestCount + 1)
        setLoading(false)
      }}
    >
      Resend OTP
      {countdown > 0 && (
        <span className="w-14 text-left">
          {countdown > 0 ? ` in (${countdown}s)` : ""}
        </span>
      )}
    </Button>
  )
}
